import { useState, useContext } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../../../hooks/useApi';
import apiService from '../../../services/apiService';
import Table from '../../../components/Table';
import Modal from '../../../components/Modal';
import { FaEye, FaTimes, FaCheck, FaComment } from 'react-icons/fa';
import { AuthContext } from '../../../context/AuthContext';

function SpecialistConsultations() {
  const { user, isAuthenticated } = useContext(AuthContext);
  const navigate = useNavigate();
  const { data: consultations, loading, error, fetchData } = useApi(`/consultations/specialist/${user?.id || ''}`);
console.log("consultations",consultations)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState(null);

  // Redirect to login if not authenticated or not a specialist
  if (!isAuthenticated()) {
    console.log('Not authenticated, redirecting to login');
    console.log('AuthContext user:', user);
    toast.error('Please log in to view your consultations.');
    return <Navigate to="/login" />;
  }
  if (user?.role !== 'specialist') {
    console.log('User role is not specialist:', user?.role);
    console.log('AuthContext user:', user);
    toast.error('Access restricted to specialists.');
    return <Navigate to="/" />;
  }

  const handleAcceptConsultation = async (id) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found in localStorage for PUT /consultations');
      console.log('AuthContext user:', user);
      console.log('localStorage contents:', JSON.stringify(localStorage));
      toast.error('Session expired. Please log in again.');
      navigate('/login');
      return;
    }

    try {
      console.log('PUT Request - Consultation ID:', id);
      console.log('Token:', token);
      console.log('Headers:', {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      });
      console.log('Body:', { status: 'accepted' });

      await apiService.put(`/consultations/status/${id}`, { status: 'accepted' });  
      toast.success('Consultation accepted successfully');
      await fetchData();
    } catch (error) {
      console.error('Accept consultation error:', error);
      console.log('Error response:', error.response?.data);
      toast.error(error.response?.data?.error || 'Failed to accept consultation');
    }
  };

  const handleDeclineConsultation = async (id) => {
    if (window.confirm('Are you sure you want to decline this consultation?')) {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found in localStorage for PUT /consultations');
        console.log('AuthContext user:', user);
        console.log('localStorage contents:', JSON.stringify(localStorage));
        toast.error('Session expired. Please log in again.');
        navigate('/login');
        return;
      }

      try {
        console.log('PUT Request - Consultation ID:', id);
        console.log('Token:', token);
        console.log('Headers:', {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });
        console.log('Body:', { status: 'declined' });

       await apiService.put(`/consultations/status/${id}`, { status: 'declined' });
        toast.success('Consultation declined successfully');
        await fetchData();
      } catch (error) {
        console.error('Decline consultation error:', error);
        console.log('Error response:', error.response?.data);
        toast.error(error.response?.data?.error || 'Failed to decline consultation');
      }
    }
  };

  const handleStartChat = (consultationId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error('No token found in localStorage for chat navigation');
      console.log('AuthContext user:', user);
      console.log('localStorage contents:', JSON.stringify(localStorage));
      toast.error('Session expired. Please log in again.');
      navigate('/login');
      return;
    }
    console.log('Navigating to chat for consultation:', consultationId);
    navigate(`/user/chat/${consultationId}`);
  };

  const openViewModal = (consultation) => {
    setSelectedConsultation(consultation);
    setIsViewModalOpen(true);
  };

  const columns = [
    {
      key: 'user',
      label: 'User/Farmer',
      render: (item) => item.userId?.fullName || 'N/A',
    },
    {
      key: 'symptoms',
      label: 'Symptoms',
      render: (item) => item.symptoms?.join(', ') || 'N/A',
    },
    {
      key: 'consultationType',
      label: 'Type',
      render: (item) => item.consultationType || 'N/A',
    },
    {
      key: 'bookingDateTime',
      label: 'Booking Date & Time',
      render: (item) => item.bookingDateTime ? new Date(item.bookingDateTime).toLocaleString() : 'N/A',
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => item.status || 'N/A',
    },
    // {
    //   key: 'disease',
    //   label: 'Disease',
    //   render: (item) => item.diseaseId?.name || item.specialistDiagnosis?.diseaseId?.name || 'N/A',
    // },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <div className="flex space-x-2">
          <button
            onClick={() => openViewModal(item)}
            className="text-blue-500 hover:text-blue-700"
            title="View"
          >
            <FaEye />
          </button>
          {item.status === 'pending' && (
            <>
              <button
                onClick={() => handleAcceptConsultation(item._id)}
                className="text-green-500 hover:text-green-700"
                title="Accept"
              >
                <FaCheck />
              </button>
              <button
                onClick={() => handleDeclineConsultation(item._id)}
                className="text-red-500 hover:text-red-700"
                title="Decline"
              >
                <FaTimes />
              </button>
            </>
          )}
          {item.status === 'accepted' && (
            <button
              onClick={() => handleStartChat(item._id)}
              className="text-indigo-500 hover:text-indigo-700"
              title="Start Chat"
            >
              <FaComment />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-5 dark:bg-gray-900 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">My Consultations</h2>
      </div>
      {loading ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          Loading...
        </div>
      ) : error ? (
        <div className="card text-center text-red-600 dark:text-red-400 p-5 rounded-lg shadow-md">
          Error: {error}
        </div>
      ) : !Array.isArray(consultations) || consultations.length === 0 ? (
        <div className="card text-center text-gray-600 dark:text-gray-300 p-5 rounded-lg shadow-md">
          No consultations found.
        </div>
      ) : (
        <div className="card bg-white dark:bg-gray-800 p-5 rounded-lg shadow-md">
          <Table columns={columns} data={consultations} />
        </div>
      )}

      <Modal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedConsultation(null);
        }}
        title="View Consultation Details"
        className="max-w-full lg:max-w-3xl mx-auto"
      >
        <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          {selectedConsultation && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    User/Farmer
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {selectedConsultation.userId?.fullName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Symptoms
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {selectedConsultation.symptoms?.join(', ') || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Consultation Type
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {selectedConsultation.consultationType || 'N/A'}
                  </p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Booking Date & Time
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {selectedConsultation.bookingDateTime ? new Date(selectedConsultation.bookingDateTime).toLocaleString() : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Status
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {selectedConsultation.status || 'N/A'}
                  </p>
                </div>
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Disease
                  </label>
                  <p className="text-base text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-gray-700 p-3 rounded-md">
                    {selectedConsultation.diseaseId?.name || selectedConsultation.specialistDiagnosis?.diseaseId?.name || 'N/A'}
                  </p>
                </div> */}
              </div>
            </div>
          )}
          <div className="flex justify-end mt-6">
            <button
              type="button"
              onClick={() => {
                setIsViewModalOpen(false);
                setSelectedConsultation(null);
              }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition duration-200 shadow-md"
            >
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SpecialistConsultations;